import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  Divider,
  Avatar,
  Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import {
  CustomCakeData,
  getSelectedCakeLineTotal,
  getSelectedCakeUnitPrice,
} from './CustomCakeBuilder';
import { getAssetURl } from '../../../../utils/url';
import { useAppSelector } from '../../../../store';

interface CakeDetailsDialogProps {
  open: boolean;
  cake: CustomCakeData | null;
  cakeIndex: number | null;
  onClose: () => void;
  onEdit: (index: number, cake: CustomCakeData) => void;
  onRemove: (index: number) => void;
  onQuantityChange: (index: number, newQty: number) => void;
  mergedPreviewImage?: string | null;
}

const CakeDetailsDialog: React.FC<CakeDetailsDialogProps> = ({
  open,
  cake,
  cakeIndex,
  onClose,
  onEdit,
  onRemove,
  onQuantityChange,
  mergedPreviewImage,
}) => {
  const assetUrl = useAppSelector(
    (state) => state.active.selectedItem.assetUrl
  );

  if (!cake || cakeIndex === null) {
    return null;
  }

  // Parse image data - handle both separate fields (new cakes) and JSON format (from orders)
  const getImageData = () => {
    // First check if cake already has separate fields (new format in Create Order flow)
    if (cake.customImageUrl !== undefined || (cake.printImageUrl && !cake.printImageUrl.startsWith('{'))) {
      return {
        printImageUrl: cake.printImageUrl || null,
        customImageUrl: cake.customImageUrl || null,
      };
    }
    
    // Otherwise, try to parse from printImageUrl (may contain JSON from existing orders)
    if (!cake.printImageUrl) {
      return { printImageUrl: null, customImageUrl: null };
    }
    
    try {
      // Try to parse as JSON (format from existing orders)
      const parsed = JSON.parse(cake.printImageUrl);
      return {
        printImageUrl: parsed.printImageUrl || null,
        customImageUrl: parsed.customImageUrl || null,
      };
    } catch {
      // If not JSON, treat as old format (only printImageUrl string)
      return {
        printImageUrl: cake.printImageUrl,
        customImageUrl: null,
      };
    }
  };

  const imageData = getImageData();

  const handleEdit = () => {
    onEdit(cakeIndex, cake);
    onClose();
  };

  const handleRemove = () => {
    if (window.confirm(`Are you sure you want to remove this cake?`)) {
      onRemove(cakeIndex);
      onClose();
    }
  };

  const handleQuantityDecrease = () => {
    if (cake.qty > 1) {
      onQuantityChange(cakeIndex, cake.qty - 1);
    }
  };

  const handleQuantityIncrease = () => {
    onQuantityChange(cakeIndex, cake.qty + 1);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant='h6'>Cake Details #{cakeIndex + 1}</Typography>
          <IconButton
            aria-label='close'
            onClick={onClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          {/* Preview Image */}
          {mergedPreviewImage ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <Avatar
                imgProps={{ crossOrigin: 'anonymous' }}
                src={mergedPreviewImage}
                sx={{
                  width: 200,
                  height: 200,
                  borderRadius: 'unset',
                  objectFit: 'contain',
                  '& img': { objectFit: 'contain' },
                  border: '1px solid #ddd',
                  bgcolor: 'background.paper',
                }}
                variant='square'
              />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3, minHeight: 200, alignItems: 'center' }}>
              <Typography variant='body2' color='text.secondary'>
                Preview image not available
              </Typography>
            </Box>
          )}

          <Grid container spacing={2}>
            {/* Shape */}
            <Grid item xs={12}>
              <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant='caption' color='text.secondary' display='block'>
                  Shape
                </Typography>
                <Typography variant='body1' fontWeight='medium'>
                  {cake.shapeLabelEn}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  {cake.shapeLabelAr}
                </Typography>
              </Box>
            </Grid>

            {/* Flavor */}
            <Grid item xs={12}>
              <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant='caption' color='text.secondary' display='block'>
                  Flavor
                </Typography>
                <Typography variant='body1' fontWeight='medium'>
                  {cake.flavorLabelEn}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  {cake.flavorLabelAr}
                </Typography>
              </Box>
            </Grid>

            {/* Color */}
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant='caption' color='text.secondary' display='block'>
                  Color
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      backgroundColor: cake.colorCode,
                      border: '1px solid #ccc',
                      borderRadius: 1,
                    }}
                  />
                  <Typography variant='body1' fontWeight='medium'>
                    {cake.colorCode}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Display Mode */}
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant='caption' color='text.secondary' display='block'>
                  Display Mode
                </Typography>
                <Typography variant='body1' fontWeight='medium' sx={{ textTransform: 'capitalize' }}>
                  {cake.displayMode === 'front' ? 'Front View' : 'Top View'}
                </Typography>
              </Box>
            </Grid>

            {/* Topping */}
            <Grid item xs={12}>
              <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant='caption' color='text.secondary' display='block'>
                  Topping
                </Typography>
                {cake.toppingId ? (
                  <>
                    <Typography variant='body1' fontWeight='medium'>
                      {cake.toppingLabelEn || 'Topping Selected'}
                    </Typography>
                    {cake.toppingLabelAr && (
                      <Typography variant='caption' color='text.secondary'>
                        {cake.toppingLabelAr}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography variant='body1' fontWeight='medium'>
                    Without (بدون)
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Custom Image (if exists) */}
            {imageData.customImageUrl && (
              <Grid item xs={12}>
                <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant='caption' color='text.secondary' display='block' gutterBottom>
                    Custom Image
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                    <img
                      src={imageData.customImageUrl.startsWith('data:') ? imageData.customImageUrl : getAssetURl(imageData.customImageUrl, assetUrl)}
                      alt='Custom cake image'
                      style={{
                        maxWidth: '100%',
                        maxHeight: '150px',
                        objectFit: 'contain',
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                      }}
                    />
                  </Box>
                </Box>
              </Grid>
            )}

            {/* Print Image (if exists) */}
            {imageData.printImageUrl && (
              <Grid item xs={12}>
                <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant='caption' color='text.secondary' display='block' gutterBottom>
                    Print Image on Cake
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                    <img
                      src={imageData.printImageUrl.startsWith('data:') ? imageData.printImageUrl : getAssetURl(imageData.printImageUrl, assetUrl)}
                      alt='Print image for cake'
                      style={{
                        maxWidth: '100%',
                        maxHeight: '150px',
                        objectFit: 'contain',
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                      }}
                    />
                  </Box>
                </Box>
              </Grid>
            )}

            {/* Write on Cake */}
            <Grid item xs={12}>
              <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant='caption' color='text.secondary' display='block'>
                  Write on Cake
                </Typography>
                <Typography variant='body1' fontWeight='medium'>
                  {cake.writeOnCake || '-'}
                </Typography>
              </Box>
            </Grid>

            {/* Premium Packaging */}
            <Grid item xs={12}>
              <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant='caption' color='text.secondary' display='block'>
                  Premium Packaging
                </Typography>
                <Typography variant='body1' fontWeight='medium'>
                  {cake.cakeBoxLabelEn || 'None'}
                </Typography>
              </Box>
            </Grid>

            {/* Instructions */}
            <Grid item xs={12}>
              <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant='caption' color='text.secondary' display='block'>
                  Instructions
                </Typography>
                <Typography variant='body1' fontWeight='medium'>
                  {cake.instruction || '-'}
                </Typography>
              </Box>
            </Grid>

            {/* Quantity */}
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant='caption' color='text.secondary' display='block' gutterBottom>
                  Quantity
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <IconButton
                    size='small'
                    onClick={handleQuantityDecrease}
                    disabled={cake.qty <= 1}
                    sx={{
                      border: '1px solid #ddd',
                      '&:disabled': {
                        opacity: 0.5,
                      },
                    }}
                  >
                    <RemoveIcon fontSize='small' />
                  </IconButton>
                  <Typography variant='h6' sx={{ minWidth: 40, textAlign: 'center' }}>
                    {cake.qty}
                  </Typography>
                  <IconButton
                    size='small'
                    onClick={handleQuantityIncrease}
                    sx={{
                      border: '1px solid #ddd',
                    }}
                  >
                    <AddIcon fontSize='small' />
                  </IconButton>
                </Box>
              </Box>
            </Grid>

            {/* Pricing */}
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant='caption' color='text.secondary' display='block'>
                  Unit Price
                </Typography>
                <Typography variant='h6' color='primary'>
                  {getSelectedCakeUnitPrice(cake).toFixed(2)} QAR
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant='caption' color='text.secondary' display='block'>
                  Total
                </Typography>
                <Typography variant='h6' color='primary' fontWeight='bold'>
                  {getSelectedCakeLineTotal(cake).toFixed(2)} QAR
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  ({cake.qty} × {getSelectedCakeUnitPrice(cake).toFixed(2)})
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={handleEdit}
          variant='contained'
          startIcon={<EditIcon />}
          sx={{
            backgroundColor: '#EE3750 !important',
            '&:hover': {
              backgroundColor: '#EE3750 !important',
            },
          }}
        >
          Edit Cake
        </Button>
        <Button
          onClick={handleRemove}
          variant='outlined'
          color='error'
          startIcon={<DeleteIcon />}
        >
          Remove Cake
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CakeDetailsDialog;

